//libs
import React, { useState, memo, useRef, useMemo, useEffect, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import SizeChart from './components/SizeChart';
import SizeButton from './components/SizeButton';
import MeasureWidth from 'shared/components/MeasureWidth';
import SizeGuideBottomSheet from './components/SizeGuideBottomSheet/SizeGuideBottomSheet';
import SelectedSizeChartStrip from 'shared/components/SelectedSizeChartStrip';
import SizeInventory from 'shared/layouts/DetailsPage/components/ProductInfo/components/SizeOptions/components/SizeInventory';

// hooks
import { useSelectedSKUSizeGuide } from 'shared/store/details/hooks/selectors';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// constants
import {
  TEXTS,
  TRACKING_KEY,
  WIDTH_OFFSET,
  SIZE_SECTION_V2_CONFIG,
  WINDOW_SCROLL_OFFSET,
  SCROLL_DELAY,
} from './texts';
import { SHOW_ANIMATION_KEY } from 'shared/layouts/SizeGuide/Components/SizeGuideComponent/constant';

// analytics
import { onReferSizeGuideClickTracking, trackSizeGuideChartClosed } from './analytics';

// styles
import {
  SizeOptionContainer,
  SizeBtnContainer,
  SizeGuideButton,
  TitleContainerV2,
  TertiaryTitleV2,
  SizeGuideText,
  BottomSheetContainer,
  SizeGuideNudgeContainer,
} from './styles';

// helpers
import {
  getSizeOptionsTrackingData,
  updateDataLayerOnSizeSelect,
} from 'shared/components/SizeSelector/helper';
import { trackSizeSelection } from './helpers';

export interface ISizeOptionProps {
  sizeOptions: ProductDetail.IProduct['sizeOptions'];
  onSelect: (size: ProductDetail.ISizeOption) => void;
  isOneSize: boolean;
  shake?: boolean;
  currentSize?: ProductDetail.ISizeOption;
  isNewSizeChartEnabled?: boolean;
  isMobile?: boolean;
  sizeGuideDataAvailable?: boolean;
  isBottomSheet?: boolean;
  isOncart?: boolean;
  disabled?: boolean;
  isDesktopWishlist?: boolean;
}

const TABLE_SCROLL_OFFSET = 150;

const getSizeDataForAllSizes = (sizeOptions: ISizeOptionProps['sizeOptions']) => {
  const sizeData = {};
  let conversionCount = 0;
  sizeOptions.options.forEach(({ sku, size_data }) => {
    sizeData[sku] = sizeOptions?.meta?.units?.reduce((acc, { label: unitLabel, id: unitId }) => {
      acc[unitLabel] = sizeOptions?.meta?.columns?.reduce(
        (innerAcc, { id: columnId, type, label: columnLabel, conversion }) => {
          if (conversion) conversionCount += 1;
          innerAcc[columnId] = {
            type,
            value: size_data?.[columnId]?.[unitId],
            label: columnLabel,
            conversion,
            unitLabel,
          };

          return innerAcc;
        },
        {}
      );
      return acc;
    }, {});
  });
  return { sizeData, showToggle: conversionCount !== sizeOptions.options.length };
};

const SizeOptions = ({
  sizeOptions,
  onSelect,
  isOneSize,
  shake,
  currentSize,
  isNewSizeChartEnabled = false,
  isMobile = false,
  sizeGuideDataAvailable = false,
  isBottomSheet = false,
  isOncart = false,
  disabled = false,
  isDesktopWishlist = false,
}: ISizeOptionProps) => {
  const [openSizeChart, setOpenSizeChart] = useState(false);
  const [measuredWidth, setMeasureWidth] = useState(0);
  const sizeGuideURL = useSelectedSKUSizeGuide();
  const contRef = useRef<HTMLDivElement>(null);
  const sizeTrackingData = useRef({ selectedSizesList: [], selectedSize: '' });
  const isMeasurementChartV2 = useRemoteConfig(SIZE_SECTION_V2_CONFIG, false);
  const sizeChartRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sizeOptions.options.length === 1) onSelect(sizeOptions.options[0]);
  }, [onSelect, sizeOptions.options]);

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(SHOW_ANIMATION_KEY) !== 'true')
        window.sessionStorage.setItem(SHOW_ANIMATION_KEY, JSON.stringify(false));
    } catch (err) {
      //do nothing
    }
  }, []);

  const sizeDataForAllSizes = useMemo(() => getSizeDataForAllSizes(sizeOptions), [sizeOptions]);
  const openSizeGuideChart = useCallback(() => {
    onReferSizeGuideClickTracking(TRACKING_KEY);
    setOpenSizeChart(true);
  }, []);

  const onSizeOptionClick = useCallback(
    (sizeOption: ProductDetail.ISizeOption) => {
      if (disabled) return;
      onSelect(sizeOption);
      const { availableSizes, outOfStockSizes } = getSizeOptionsTrackingData(sizeOptions.options);
      const sizesList: string[] = sizeTrackingData.current.selectedSizesList;
      if (isMeasurementChartV2 && isMobile) {
        setTimeout(() => {
          const bottomPosition =
            sizeChartRef.current?.getBoundingClientRect().bottom + window.pageYOffset;
          const scrollToPosition = bottomPosition - window.innerHeight + TABLE_SCROLL_OFFSET;

          if (
            !isBottomSheet &&
            window.innerHeight - sizeChartRef.current?.getBoundingClientRect().bottom <
              WINDOW_SCROLL_OFFSET
          ) {
            window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
          }
        }, SCROLL_DELAY);
      }

      sizesList.push(sizeOption.sizeName);
      sizeTrackingData.current = {
        selectedSizesList: sizesList,
        selectedSize: sizeOption.sizeName,
      };
      trackSizeSelection(sizeTrackingData.current);
      updateDataLayerOnSizeSelect(availableSizes, outOfStockSizes, sizeOption);
    },
    [sizeOptions.options, isMeasurementChartV2, isMobile, isBottomSheet, onSelect, disabled]
  );

  const checkStockStatus = useCallback(() => {
    let greatestString = '';

    for (const val of sizeOptions.options) {
      if (val.sizeName.length > greatestString.length) {
        greatestString = val.sizeName;
      }
    }

    return { greatestString };
  }, [sizeOptions.options]);

  const { greatestString } = checkStockStatus();
  const showSizeGuideButton = isNewSizeChartEnabled
    ? sizeGuideDataAvailable
    : !isEmpty(sizeGuideURL);

  const handleSizeGuideClose = useCallback(() => {
    setOpenSizeChart(false);
    trackSizeGuideChartClosed();
  }, []);

  const selectedSizeChartStripData = useMemo(() => {
    if (!currentSize || !currentSize.sku) {
      return null; // a fallback value if needed
    }
    return (
      sizeDataForAllSizes?.sizeData[currentSize.sku]?.in ||
      sizeDataForAllSizes?.sizeData[currentSize.sku]
    );
  }, [currentSize, sizeDataForAllSizes.sizeData]);

  // handle inventory Nudge for few sizes in limited stock
  if (isEmpty(sizeOptions) || isOneSize) {
    return null;
  }

  return (
    <SizeOptionContainer
      className="size-options-container"
      data-test="size-options-v2"
      shake={shake}
    >
      {!isBottomSheet && (
        <>
          <TitleContainerV2>
            <TertiaryTitleV2>{sizeOptions.title}</TertiaryTitleV2>
            <SizeGuideText>
              {showSizeGuideButton && (
                <SizeGuideButton
                  onClick={openSizeGuideChart}
                  data-test="btn-size-chart"
                  isSizeSelectorV2={true}
                >
                  {TEXTS.SIZE_GUIDE}
                </SizeGuideButton>
              )}
            </SizeGuideText>
          </TitleContainerV2>
          <SizeGuideNudgeContainer>
            <SizeInventory />
          </SizeGuideNudgeContainer>
        </>
      )}

      <SizeBtnContainer ref={contRef}>
        <MeasureWidth onMeasured={setMeasureWidth}>
          <SizeButton
            onClick={() => {}}
            isSelected={false}
            isOutOfStock={false}
            size={greatestString}
            containerRef={contRef}
            stockRemaining={4}
            isSizeButtonV2={true}
          />
        </MeasureWidth>

        {measuredWidth !== 0 &&
          sizeOptions.options.map((sizeOption, i) => (
            <SizeButton
              key={i}
              onClick={() => onSizeOptionClick(sizeOption)}
              isSelected={currentSize?.sku === sizeOption.sku}
              isOutOfStock={sizeOption.isOutOfStock === 1}
              size={sizeOption.sizeName}
              containerRef={contRef}
              stockRemaining={sizeOption.stock_remaining}
              finalWidth={isOncart ? 0 : measuredWidth - WIDTH_OFFSET}
              isSizeButtonV2={true}
              disabled={disabled}
            />
          ))}
      </SizeBtnContainer>

      {isMeasurementChartV2 &&
        currentSize?.sku &&
        !isEmpty(sizeDataForAllSizes.sizeData[currentSize.sku]) && (
          <div ref={sizeChartRef}>
            {isBottomSheet ? (
              <BottomSheetContainer data-set="bottomsheet">
                <SelectedSizeChartStrip
                  sizeData={sizeDataForAllSizes.sizeData[currentSize.sku]?.in}
                  sizeName={currentSize.sizeName}
                  isBottomSheet={isBottomSheet}
                  isDesktopWishlist={isDesktopWishlist}
                />
              </BottomSheetContainer>
            ) : (
              <SelectedSizeChartStrip
                sizeData={selectedSizeChartStripData}
                sizeName={currentSize.sizeName}
                isDesktopWishlist={isDesktopWishlist}
              />
            )}
          </div>
        )}

      {!isNewSizeChartEnabled && openSizeChart && (
        <SizeChart sizeGuideURL={sizeGuideURL} onRemove={handleSizeGuideClose} />
      )}

      {isNewSizeChartEnabled && sizeGuideDataAvailable && (
        <SizeGuideBottomSheet
          isMobile={isMobile}
          showSizeChart={openSizeChart}
          onClose={handleSizeGuideClose}
        />
      )}
    </SizeOptionContainer>
  );
};

export default memo(SizeOptions);
